import { useCallback, useEffect, useMemo, useState } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';
import { Control, FieldValues, Path, PathValue, UseFormSetValue, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { normalizeColorHex } from '@/lib/utils';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';

import { SingleSelect, SingleSelectOption } from './single-select';

interface PaginatedLoadFunction {
  (cursor?: number | null): Promise<PaginatedResponse<DropdownSearchItem>>;
}

interface PaginatedSearchFunction {
  (searchTerm: string, cursor?: number | null): Promise<PaginatedResponse<DropdownSearchItem>>;
}

interface SingleSelectWithPaginationProps<T extends FieldValues> {
  // Form integration props (same as SingleSelect)
  name: Path<T>;
  /**
   * Optional field name to store the display name (label) of the selected item.
   * When provided, the component will automatically sync this field with the item's name.
   * This is useful when you need both the ID and the human-readable name (e.g., country code and country name).
   *
   * **Important**: When using `displayName`, you MUST also provide `setValue` prop.
   *
   * @example
   * ```tsx
   * // Form has both 'country' (stores code like "US") and 'countryName' (stores "United States")
   * <SingleSelectWithPagination
   *   name="country"
   *   displayName="countryName"
   *   control={form.control}
   *   setValue={form.setValue}  // Required when displayName is provided
   *   // ...other props
   * />
   * ```
   */
  displayName?: Path<T>;
  control: Control<T>;
  /**
   * React Hook Form's setValue function. Required when using `displayName` prop.
   * Used to programmatically update form values when the component resolves IDs to display names.
   *
   * @example
   * ```tsx
   * <SingleSelectWithPagination
   *   name="clientId"
   *   control={form.control}
   *   setValue={form.setValue}  // Pass form.setValue here
   *   // ...other props
   * />
   * ```
   */
  setValue?: UseFormSetValue<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  showFormMessage?: boolean;

  // Visual customization (from SingleSelect)
  showAvatars?: boolean;
  showDescriptionInOptions?: boolean;
  showDescriptionInSelector?: boolean;
  avatarShape?: 'circle' | 'square' | 'rounded';
  avatarSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  maxHeight?: string;
  /** Enable clear selection button. Default: true */
  isClearSelectionEnabled?: boolean;

  // Custom rendering
  renderOption?: (option: SingleSelectOption, isSelected: boolean) => React.ReactNode;
  renderTrigger?: (
    selectedOption: SingleSelectOption | null,
    placeholder: string
  ) => React.ReactNode;
  /**
   * Derives a section key from each loaded item — options with the same
   * consecutive key are grouped under a shared heading (see `renderGroupHeading`).
   * Items are expected to already arrive pre-sorted by group.
   */
  getGroupKey?: (item: DropdownSearchItem) => string | undefined;
  /** Renders the heading for a section produced by `getGroupKey`. */
  renderGroupHeading?: (groupKey: string) => React.ReactNode;

  // Callbacks
  onOpenChange?: (open: boolean) => void;
  showWarning?: (item: DropdownSearchItem) => string | undefined;
  /**
   * Callback fired when an item is selected.
   * Provides the full item object, not just the ID.
   *
   * @example
   * ```tsx
   * <SingleSelectWithPagination
   *   name="projectId"
   *   onSelect={(item) => {
   *     console.log(item); // { id, name, secondaryText, colorHex, ... }
   *   }}
   *   // ...other props
   * />
   * ```
   */
  onSelect?: (item: DropdownSearchItem | null) => void;

  // Pagination functions
  loadItems: PaginatedLoadFunction;
  searchItems?: PaginatedSearchFunction;
  onDataLoad?: (items: DropdownSearchItem[], total: number) => void;

  // Query key for caching (required for TanStack Query)
  queryKey: string[];

  /**
   * How long fetched pages stay fresh before a background refetch, in ms.
   * Default: 5 minutes. Raise this for data that changes rarely and is
   * explicitly invalidated elsewhere (see `@/modules/common/utils/query-invalidation`)
   * to cut down on redundant requests when the dropdown is reopened.
   */
  staleTime?: number;

  /**
   * @deprecated Use `initialSelectedItem` instead. This prop triggers unnecessary API calls
   * before the dropdown is opened just to resolve display names.
   *
   * Prefer providing the initial selected item data directly via `initialSelectedItem` prop.
   */
  fetchBeforeOpen?: boolean;

  /**
   * Initial selected item data for edit forms.
   * When provided, the component will display this item immediately without needing to fetch data.
   * Once the dropdown is opened and data is loaded, the item will be matched/updated from the API response.
   *
   * This eliminates the need for `fetchBeforeOpen` by providing the display data upfront.
   *
   * @example
   * ```tsx
   * // In an edit form where you already have the selected item data
   * <SingleSelectWithPagination
   *   name="managerId"
   *   control={form.control}
   *   queryKey={['managers']}
   *   loadItems={loadManagers}
   *   initialSelectedItem={{
   *     id: project.manager.id.toString(),
   *     name: project.manager.name,
   *     secondaryText: project.manager.email,
   *     avatarLink: project.manager.avatar
   *   }}
   * />
   * ```
   */
  initialSelectedItem?: DropdownSearchItem;

  /**
   * Additional items to inject into the dropdown options.
   * Useful for including items that won't be returned by the API (e.g., deleted items).
   * These items will be prepended to the fetched items and won't be duplicated if they already exist.
   *
   * @example
   * ```tsx
   * // Include a deleted manager that won't be returned by the API
   * <SingleSelectWithPagination
   *   name="managerId"
   *   additionalItems={projectData?.info.manager?.isDeleted ? [{
   *     id: projectData.info.manager.id.toString(),
   *     name: projectData.info.manager.name,
   *     isDeleted: true,
   *     secondaryText: projectData.info.manager.email
   *   }] : undefined}
   *   // ...other props
   * />
   * ```
   */
  additionalItems?: DropdownSearchItem[];

  /**
   * IDs to exclude from the dropdown options.
   * Useful for preventing self-selection (e.g., a team member cannot be their own manager).
   *
   * @example
   * ```tsx
   * <SingleSelectWithPagination
   *   name="managerId"
   *   excludeIds={[currentTeamMemberId]}
   *   // ...other props
   * />
   * ```
   */
  excludeIds?: string[];

  /**
   * When true, renders an "Add '{search}'" item at the bottom of the dropdown whenever
   * the search term does not match any existing option label (case-insensitive).
   * Requires `onCreateOption` to be provided.
   */
  isCreatable?: boolean;
  /**
   * Callback invoked when the user selects the synthetic add option.
   * Should return (or resolve to) a SingleSelectOption — its `value` becomes the form value.
   * Use this to create the entity locally (e.g. set a pending marker) or remotely.
   */
  onCreateOption?: (inputValue: string) => SingleSelectOption | Promise<SingleSelectOption>;
}

const MIN_INPUT_CHARS_FOR_SEARCH = 2;
const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000;

/**
 * SingleSelect component with pagination and async data loading support.
 * Combines the UI/UX of SingleSelect with paginated data loading capabilities.
 *
 * Features:
 * - Async data loading with cursor-based pagination using TanStack Query
 * - Automatic caching (5 minutes stale time, 10 minutes cache time)
 * - Search functionality with 2+ character minimum and 250ms debouncing
 * - Infinite scroll support
 * - Loading states and error handling
 * - All SingleSelect customization options
 * - Optional displayName field support for storing both ID and display name
 * - Initial selected item support for edit forms (no pre-fetching needed)
 *
 * @example Basic usage
 * ```tsx
 * <SingleSelectWithPagination
 *   name="clientId"
 *   control={form.control}
 *   setValue={form.setValue}
 *   queryKey={['clients-short']}
 *   loadItems={cursor => getShortClients({ pagination: { cursor, pageSize: 10 } })}
 *   searchItems={(searchTerm, cursor) =>
 *     getShortClients({
 *       filter: { searchString: searchTerm },
 *       pagination: { cursor, pageSize: 10 },
 *     })
 *   }
 * />
 * ```
 *
 * @example With initialSelectedItem (recommended for edit forms)
 * ```tsx
 * // Display selected item immediately without pre-fetching data
 * <SingleSelectWithPagination
 *   name="managerId"
 *   control={form.control}
 *   queryKey={['managers']}
 *   loadItems={loadManagers}
 *   searchItems={searchManagers}
 *   initialSelectedItem={{
 *     id: project.manager.id.toString(),
 *     name: project.manager.name,
 *     secondaryText: project.manager.email,
 *     avatarLink: project.manager.avatar
 *   }}
 * />
 * ```
 *
 * @example With displayName (stores both ID and display name)
 * ```tsx
 * <SingleSelectWithPagination
 *   name="country"           // Stores country code (e.g., "US")
 *   displayName="countryName" // Stores country name (e.g., "United States")
 *   control={form.control}
 *   setValue={form.setValue}  // Required when using displayName
 *   queryKey={['countries']}
 *   loadItems={loadCountries}
 *   searchItems={searchCountries}
 * />
 * ```
 *
 * @example With additionalItems (include items not returned by API)
 * ```tsx
 * // Include a deleted manager in the dropdown
 * <SingleSelectWithPagination
 *   name="managerId"
 *   control={form.control}
 *   queryKey={['managers']}
 *   loadItems={loadManagers}
 *   searchItems={searchManagers}
 *   additionalItems={manager?.isDeleted ? [{
 *     id: manager.id.toString(),
 *     name: manager.name,
 *     secondaryText: manager.email,
 *     isDeleted: true
 *   }] : undefined}
 *   showWarning={(item) => item.isDeleted ? 'This manager has been deleted' : undefined}
 * />
 * ```
 */
export function SingleSelectWithPagination<T extends FieldValues>({
  // Form props
  name,
  displayName,
  control,
  setValue,
  label,
  placeholder,
  disabled = false,
  loading: externalLoading = false,
  className,
  showFormMessage = true,

  // Visual customization
  showAvatars = false,
  showDescriptionInOptions = false,
  showDescriptionInSelector = false,
  avatarShape = 'circle',
  avatarSize,
  maxHeight = '300px',
  isClearSelectionEnabled = false,

  // Custom rendering
  renderOption,
  renderTrigger,
  getGroupKey,
  renderGroupHeading,

  // Pagination props
  loadItems,
  searchItems,
  onDataLoad,
  onOpenChange,
  queryKey,
  staleTime = DEFAULT_STALE_TIME_MS,
  // @deprecated - use initialSelectedItem instead
  fetchBeforeOpen = false,
  // Initial selected item data for edit forms
  initialSelectedItem,
  // Define custom warning condition
  showWarning,
  // Callback when item is selected
  onSelect,
  // Additional items to inject
  additionalItems,
  // IDs to exclude from options
  excludeIds,
  // Inline create option
  isCreatable = false,
  onCreateOption,
}: SingleSelectWithPaginationProps<T>) {
  const { t } = useTranslation(['common']);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<DropdownSearchItem | null>(null);
  const [warning, setWarning] = useState<string>();

  // Watch the form field value to track selected item
  const fieldValue = useWatch({ control, name });
  // Always call useWatch to satisfy React Hooks rules, but only use the value if displayName is provided
  const _displayFieldValue = useWatch({
    control,
    name: (displayName || name) as Path<T>, // Fallback to name if displayName not provided
  });
  const displayFieldValue = displayName ? _displayFieldValue : undefined;

  // Debounce search term changes
  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setDebouncedSearchTerm(value);
  }, 250);

  // Use TanStack Query for data fetching with caching
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading: isInitialLoading,
  } = useInfiniteQuery({
    queryKey: [...queryKey, debouncedSearchTerm],
    enabled: isOpen || fetchBeforeOpen, // Only fetch when dropdown is open
    queryFn: async ({ pageParam = null }) => {
      const shouldSearch = searchItems && debouncedSearchTerm.length >= MIN_INPUT_CHARS_FOR_SEARCH;

      if (shouldSearch) {
        return searchItems(debouncedSearchTerm, pageParam as number | null);
      }

      return loadItems(pageParam as number | null);
    },
    getNextPageParam: lastPage => lastPage.pagination.cursor,
    initialPageParam: null,
    staleTime,
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Flatten paginated data and merge with additional items
  const items = useMemo(() => {
    const fetchedItems = data ? data.pages.flatMap(page => page.items) : [];

    let mergedItems: DropdownSearchItem[];

    // If no additional items, use fetched items as-is
    if (!additionalItems || additionalItems.length === 0) {
      mergedItems = fetchedItems;
    } else {
      // Merge additional items with fetched items, avoiding duplicates
      // Additional items are prepended so they appear at the top
      const fetchedItemIds = new Set(fetchedItems.map(item => item.id));
      const uniqueAdditionalItems = additionalItems.filter(item => !fetchedItemIds.has(item.id));
      mergedItems = [...uniqueAdditionalItems, ...fetchedItems];
    }

    // Filter out excluded IDs
    if (excludeIds && excludeIds.length > 0) {
      const excludeSet = new Set(excludeIds);
      return mergedItems.filter(item => !excludeSet.has(item.id));
    }

    return mergedItems;
  }, [data, additionalItems, excludeIds]);

  const totalCount = useMemo(() => {
    if (!data || data.pages.length === 0) return 0;
    return data.pages[data.pages.length - 1].pagination.total;
  }, [data]);

  // Notify parent of data load
  useEffect(() => {
    if (data && onDataLoad) {
      onDataLoad(items, totalCount);
    }
  }, [data, items, totalCount, onDataLoad]);

  // Initialize from initialSelectedItem when provided and data not yet loaded from API
  // This handles the initial render before dropdown is opened
  // Note: items may contain additionalItems, so we check if data is loaded instead
  useEffect(() => {
    if (
      initialSelectedItem &&
      fieldValue &&
      initialSelectedItem.id === fieldValue &&
      !selectedItem &&
      !data // No data loaded from API yet (additionalItems don't count)
    ) {
      // Set warning based on initialSelectedItem before data loads
      if (showWarning) {
        setWarning(showWarning(initialSelectedItem));
      }
      // Call onSelect with initial item for parent component awareness
      onSelect?.(initialSelectedItem);
    }

    // Handle displayName pattern: fieldValue is empty but displayFieldValue has the name
    // (e.g., country/city where backend provides name but no ID)
    if (
      initialSelectedItem &&
      !fieldValue &&
      displayFieldValue &&
      initialSelectedItem.name === displayFieldValue &&
      !selectedItem &&
      !data
    ) {
      if (showWarning) {
        setWarning(showWarning(initialSelectedItem));
      }
      onSelect?.(initialSelectedItem);
    }
  }, [
    initialSelectedItem,
    fieldValue,
    displayFieldValue,
    selectedItem,
    data,
    showWarning,
    onSelect,
  ]);

  // Update selectedItem when we find the corresponding item in our data
  useEffect(() => {
    if (fieldValue && items.length > 0) {
      const foundItem = items.find(item => item.id === fieldValue);
      if (
        foundItem &&
        (!selectedItem || selectedItem.id !== foundItem.id || selectedItem.name !== foundItem.name)
      ) {
        setSelectedItem(foundItem);
        onSelect?.(foundItem);

        showWarning && setWarning(showWarning(foundItem));

        // Automatically set displayName field if provided
        if (displayName && setValue) {
          setValue(displayName, foundItem.name as PathValue<T, Path<T>>, { shouldDirty: false });
        }
      }
    } else if (!fieldValue && displayFieldValue && items.length > 0) {
      // Handle case where we have displayName value but no ID value (e.g., initialized from backend)
      // Find the item by name and set the ID field
      const foundItem = items.find(item => item.name === displayFieldValue);
      if (foundItem && setValue) {
        setSelectedItem(foundItem);
        onSelect?.(foundItem);

        showWarning && setWarning(showWarning(foundItem));

        // Set the ID field
        setValue(name, foundItem.id as PathValue<T, Path<T>>, { shouldDirty: false });
      }
    } else if (!fieldValue && selectedItem !== null) {
      // fieldValue is cleared — clear selectedItem only if it no longer matches displayFieldValue
      // (handles both X button clear and country change resetting city)
      if (selectedItem.name !== displayFieldValue) {
        onSelect?.(null);
        setSelectedItem(null);
        setWarning(undefined);
      }
    }
  }, [
    fieldValue,
    displayFieldValue,
    items,
    selectedItem,
    showWarning,
    displayName,
    name,
    setValue,
    onSelect,
  ]);

  // Convert DropdownSearchItem to SingleSelectOption
  const convertToOptions = useCallback(
    (items: DropdownSearchItem[]): SingleSelectOption[] => {
      return items.map(item => ({
        value: item.id,
        label: item.name,
        description: item.secondaryText,
        avatar: item.avatarLink,
        color: normalizeColorHex(item.colorHex),
        status: item.status,
        nextArchiveDate: item.nextArchiveDate,
        nextUnarchiveDate: item.nextUnarchiveDate,
        isInPrimaryGroup: item.isInPrimaryGroup,
        groupKey: getGroupKey?.(item),
      }));
    },
    [getGroupKey]
  );

  // Handle value change — clear displayName and selectedItem when value is cleared (X button)
  const handleChange = useCallback(
    (value: string) => {
      if (!value) {
        setSelectedItem(null);
        setWarning(undefined);
        if (displayName && setValue) {
          setValue(displayName, '' as PathValue<T, Path<T>>, { shouldDirty: true });
        }
        onSelect?.(null);
        return;
      }

      // Resolve the label synchronously from the options currently displayed —
      // they always contain the clicked item. Relying solely on the async effect
      // that matches against the cache fails when the item was picked via search:
      // closing the dropdown resets the query key to the empty-search result set,
      // which may not include the selected item, leaving the trigger showing the
      // raw ID. Capturing it here keeps the label correct regardless.
      const picked = items.find(item => item.id === value);
      if (picked) {
        setSelectedItem(picked);
        if (showWarning) {
          setWarning(showWarning(picked));
        }
        // Sync the display-name field on user selection so consumers relying on the
        // human-readable value (and RHF dirty tracking) update immediately — the async
        // resolve effect only runs {shouldDirty:false}, which loses a genuine change.
        if (displayName && setValue) {
          setValue(displayName, picked.name as PathValue<T, Path<T>>, { shouldDirty: true });
        }
        onSelect?.(picked);
      }
    },
    [displayName, setValue, onSelect, items, showWarning]
  );

  // Handle search input from SingleSelect
  const handleSearchChange = useCallback(
    (value: string) => {
      debouncedSetSearch(value);
    },
    [debouncedSetSearch]
  );

  // Handle open/close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setDebouncedSearchTerm('');
      }
      onOpenChange?.(open);
    },
    [onOpenChange]
  );

  const options = useMemo(() => convertToOptions(items), [items, convertToOptions]);

  // Create selected option for display purposes
  // Priority: selectedItem (from loaded data) > initialSelectedItem (provided upfront) > fieldValue fallback
  const displaySelectedOption = useMemo(() => {
    // Only show selectedItem if it matches the current field state
    // When fieldValue is empty, only show selectedItem if it matches displayFieldValue
    if (selectedItem) {
      if (fieldValue && selectedItem.id === fieldValue) {
        return {
          value: selectedItem.id,
          label: selectedItem.name,
          description: selectedItem.secondaryText,
          avatar: selectedItem.avatarLink,
          color: normalizeColorHex(selectedItem.colorHex),
          status: selectedItem.status,
        };
      }
      if (!fieldValue && displayFieldValue && selectedItem.name === displayFieldValue) {
        return {
          value: selectedItem.id,
          label: selectedItem.name,
          description: selectedItem.secondaryText,
          avatar: selectedItem.avatarLink,
          color: normalizeColorHex(selectedItem.colorHex),
          status: selectedItem.status,
        };
      }
    }

    // Use initialSelectedItem if provided and matches the current fieldValue
    // This allows displaying proper item data before the dropdown is opened
    if (initialSelectedItem && fieldValue && initialSelectedItem.id === fieldValue) {
      return {
        value: initialSelectedItem.id,
        label: initialSelectedItem.name,
        description: initialSelectedItem.secondaryText,
        avatar: initialSelectedItem.avatarLink,
        color: normalizeColorHex(initialSelectedItem.colorHex),
        status: initialSelectedItem.status,
      };
    }

    // Handle displayName pattern: fieldValue is empty but displayFieldValue has the name
    // (e.g., country/city where backend provides name but no ID)
    if (
      initialSelectedItem &&
      !fieldValue &&
      displayFieldValue &&
      initialSelectedItem.name === displayFieldValue
    ) {
      return {
        value: initialSelectedItem.id,
        label: initialSelectedItem.name,
        description: initialSelectedItem.secondaryText,
        avatar: initialSelectedItem.avatarLink,
        color: normalizeColorHex(initialSelectedItem.colorHex),
        status: initialSelectedItem.status,
      };
    }

    // Fallback: if we have a fieldValue but no selectedItem yet (data not loaded),
    // create a temporary display option using the fieldValue as both id and label
    if (fieldValue) {
      return {
        value: fieldValue as string,
        label: fieldValue as string,
      };
    }

    return null;
  }, [selectedItem, initialSelectedItem, fieldValue, displayFieldValue]);

  return (
    <SingleSelect<T>
      warning={warning}
      name={name}
      control={control}
      label={label}
      placeholder={placeholder || t('selectOption')}
      className={className}
      showFormMessage={showFormMessage}
      disabled={disabled}
      isClearSelectionEnabled={isClearSelectionEnabled}
      showAvatars={showAvatars}
      showDescriptionInOptions={showDescriptionInOptions}
      showDescriptionInSelector={showDescriptionInSelector}
      avatarShape={avatarShape}
      avatarSize={avatarSize}
      maxHeight={maxHeight}
      options={options}
      selectedOption={displaySelectedOption}
      loading={externalLoading || isInitialLoading}
      searchable={true}
      hideSearchWhenEmpty={false}
      emptyMessage={t('noResultsFound')}
      noOptionsMessage={t('noOptionsAvailable')}
      searchPlaceholder={t('searchPlaceholder')}
      onOpenChange={handleOpenChange}
      onSearchChange={handleSearchChange}
      onChange={handleChange}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetching && !isInitialLoading}
      fetchNextPage={fetchNextPage}
      enableClientSideFiltering={false}
      disableVirtualization={true} // Disable virtualization for server-side pagination
      renderOption={renderOption}
      renderTrigger={renderTrigger}
      renderGroupHeading={renderGroupHeading}
      isCreatable={isCreatable}
      onCreateOption={onCreateOption}
    />
  );
}
